import type { AxiosInstance } from 'axios';

export type ReleasePublicationStatus =
  | 'unpublished'
  | 'published'
  | 'unpublishedChanges'
  | 'unknown';

export interface ReleaseEditorialSummary {
  title: string;
  revision: string;
  updatedBy: string;
  updatedAt: string;
  publishedRevision: string;
  publishedBy: string;
  publishedAt: string;
  publicationId: string;
}

export interface ComputerReleaseDraftSummary {
  version: string;
  sourceSha: string;
  runUrl: string;
  summarySource: 'model' | 'fallback';
  headline: string;
  releasePublishedAt: string;
  createdAt: string;
  publicationStatus: ReleasePublicationStatus;
  editorial?: ReleaseEditorialSummary;
}

export interface ComputerReleaseDraft extends ComputerReleaseDraftSummary {
  baseSha: string;
  bodyMarkdown: string;
  sourceSummary: Record<string, unknown>;
  editorial?: ReleaseEditorialSummary & { content: string };
}

export interface ComputerReleaseDraftPage {
  drafts: ComputerReleaseDraftSummary[];
  nextPageToken: string;
}

export interface SaveReleaseEditorial {
  expectedRevision: string;
  title: string;
  content: string;
}

export interface PublishReleaseEditorial {
  expectedRevision: string;
  requestId: string;
}

export interface ComputerReleaseNotesApi {
  list(
    pageToken: string,
    signal: AbortSignal,
  ): Promise<ComputerReleaseDraftPage>;
  get(version: string, signal: AbortSignal): Promise<ComputerReleaseDraft>;
  save(
    version: string,
    input: SaveReleaseEditorial,
    signal: AbortSignal,
  ): Promise<ComputerReleaseDraft>;
  publish(
    version: string,
    input: PublishReleaseEditorial,
    signal: AbortSignal,
  ): Promise<ComputerReleaseDraft>;
}

const collectionPath =
  '/api/desktop/v2/admin/tutti-v2/computer-release-note-drafts';

function object(value: unknown): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('发布草稿响应格式不正确');
  }
  return value as Record<string, unknown>;
}

function text(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== 'string' || !value.trim())
    throw new Error(`发布草稿缺少 ${key}`);
  return value;
}

function date(record: Record<string, unknown>, key: string): string {
  const value = text(record, key);
  if (!Number.isFinite(Date.parse(value)))
    throw new Error('发布草稿时间不正确');
  return value;
}

function revision(value: unknown, allowZero = false): string {
  if (value === undefined && allowZero) return '0';
  if (
    typeof value !== 'string' ||
    !/^(0|[1-9][0-9]{0,18})$/.test(value) ||
    BigInt(value) > 9223372036854775807n ||
    (!allowZero && value === '0')
  ) {
    throw new Error('发布草稿修订号不正确');
  }
  return value;
}

function editorialSummary(value: unknown): ReleaseEditorialSummary {
  const record = object(value);
  const currentRevision = revision(record.revision);
  const publishedRevision = revision(record.publishedRevision, true);
  if (BigInt(publishedRevision) > BigInt(currentRevision))
    throw new Error('发布修订号超过草稿修订号');
  const published = publishedRevision !== '0';
  const publicationId = published ? text(record, 'publicationId') : '';
  if (
    published &&
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
      publicationId,
    )
  ) {
    throw new Error('发布记录标识不正确');
  }
  return {
    title: text(record, 'title'),
    revision: currentRevision,
    updatedBy: text(record, 'updatedBy'),
    updatedAt: date(record, 'updatedAt'),
    publishedRevision,
    publishedBy: published ? text(record, 'publishedBy') : '',
    publishedAt: published ? date(record, 'publishedAt') : '',
    publicationId,
  };
}

function summary(value: unknown): ComputerReleaseDraftSummary {
  const record = object(value);
  const source = record.summarySource;
  if (source !== 'model' && source !== 'fallback')
    throw new Error('发布草稿摘要来源不正确');
  const runUrl = text(record, 'runUrl');
  if (
    !/^https:\/\/github\.com\/tutti-lab\/tutti\/actions\/runs\/[1-9][0-9]*$/.test(
      runUrl,
    )
  ) {
    throw new Error('发布草稿的 GitHub 任务地址不正确');
  }
  const status =
    record.publicationStatus === undefined
      ? 'unknown'
      : record.publicationStatus;
  if (
    !['unpublished', 'published', 'unpublishedChanges', 'unknown'].includes(
      String(status),
    )
  ) {
    throw new Error('发布状态不正确');
  }
  const editorial =
    record.editorial == null ? undefined : editorialSummary(record.editorial);
  const expected =
    !editorial || editorial.publishedRevision === '0'
      ? 'unpublished'
      : editorial.publishedRevision === editorial.revision
        ? 'published'
        : 'unpublishedChanges';
  if (
    (status === 'unknown' && editorial) ||
    (status !== 'unknown' && status !== expected)
  ) {
    throw new Error('发布状态与修订记录不一致');
  }
  return {
    version: text(record, 'version'),
    sourceSha: text(record, 'sourceSha'),
    runUrl,
    summarySource: source,
    headline: text(record, 'headline'),
    releasePublishedAt: date(record, 'releasePublishedAt'),
    createdAt: date(record, 'createdAt'),
    publicationStatus: status as ReleasePublicationStatus,
    editorial,
  };
}

function detail(value: unknown, version: string): ComputerReleaseDraft {
  const record = object(value);
  const editorial =
    record.editorial == null ? undefined : object(record.editorial);
  const metadata = summary({ ...record, editorial: editorial?.metadata });
  if (metadata.version !== version)
    throw new Error('返回的草稿与所选版本不一致，请重新选择');
  const baseSha = record.baseSha === undefined ? '' : record.baseSha;
  if (typeof baseSha !== 'string') throw new Error('发布草稿基线不正确');
  return {
    ...metadata,
    baseSha,
    bodyMarkdown: text(record, 'bodyMarkdown'),
    sourceSummary: object(record.sourceSummary),
    editorial:
      editorial && metadata.editorial
        ? { ...metadata.editorial, content: text(editorial, 'content') }
        : undefined,
  };
}

export function createComputerReleaseNotesApi(
  client: Pick<AxiosInstance, 'get' | 'put' | 'post'>,
  origin: string,
): ComputerReleaseNotesApi {
  const versionPath = (version: string) =>
    `${collectionPath}/${encodeURIComponent(version)}`;
  return {
    async list(pageToken, signal) {
      const response = object(
        await client.get<unknown, unknown>(collectionPath, {
          baseURL: origin,
          params: { pageSize: 20, pageToken: pageToken || undefined },
          signal,
        }),
      );
      const drafts = response.drafts === undefined ? [] : response.drafts;
      const nextPageToken =
        response.nextPageToken === undefined ? '' : response.nextPageToken;
      if (!Array.isArray(drafts) || typeof nextPageToken !== 'string')
        throw new Error('发布草稿分页响应不正确');
      return { drafts: drafts.map(summary), nextPageToken };
    },
    async get(version, signal) {
      return detail(
        await client.get<unknown, unknown>(versionPath(version), {
          baseURL: origin,
          signal,
        }),
        version,
      );
    },
    async save(version, input, signal) {
      const saved = detail(
        await client.put<unknown, unknown>(versionPath(version), input, {
          baseURL: origin,
          signal,
        }),
        version,
      );
      if (
        !saved.editorial ||
        saved.editorial.title !== input.title ||
        saved.editorial.content !== input.content
      ) {
        throw new Error('保存响应与本次编辑不一致，请重新读取版本核实');
      }
      return saved;
    },
    async publish(version, input, signal) {
      const published = detail(
        await client.post<unknown, unknown>(
          `${versionPath(version)}:publish`,
          input,
          { baseURL: origin, signal },
        ),
        version,
      );
      if (
        published.publicationStatus !== 'published' ||
        published.editorial?.publishedRevision !== input.expectedRevision
      ) {
        throw new Error('发布结果尚未确认，请重新读取版本核实');
      }
      return published;
    },
  };
}

export function releaseDraftToUpdate(draft: ComputerReleaseDraft) {
  return {
    version: draft.version,
    title: draft.editorial?.title ?? draft.headline,
    content: draft.editorial?.content ?? draft.bodyMarkdown,
  };
}

export function computerReleaseDraftErrorMessage(error: unknown): string {
  const response = (
    error as {
      response?: { status?: number; data?: { reason?: string } };
    } | null
  )?.response;
  switch (response?.status) {
    case 401:
      return '登录状态已失效，请重新登录后获取发布草稿。';
    case 403:
      return '当前账号没有管理 Computer 发布信息的权限。';
    case 404:
      return response.data?.reason === 'COMPUTER_RELEASE_NOTE_NOT_FOUND'
        ? '该版本的发布草稿不存在，请刷新版本列表。'
        : '发布草稿接口尚未可用，请确认服务端已部署。';
    case 409:
      return '该版本已被其他管理员修改，或请求与已有发布记录冲突。请重新读取版本，核对后再保存或发布。';
    case 503:
      return '发布草稿服务暂不可用，请稍后刷新。';
    default:
      return error instanceof Error
        ? error.message
        : '发布信息操作失败，请重试。';
  }
}
